import { StyledTooltip, TooltipGlobalStyle, TooltipContentWrapper, TooltipMainText, TooltipSecondaryText } from '../TooltipStyles';
import { Timer, XCircle, ArrowRightLeft } from 'lucide-react';
import { Journey } from '../../utils/mapJourneysData';
import { colors } from "../../utils/colors";
import { getColor, getReliabilityColorGradient } from "../../utils/colorFormatter";
import { useTranslation } from 'react-i18next';
import {
  StatContainer,
  StatsContainer,
  StatBox,
  StatTopSection,
  StatIconWrapper,
  StatTitle,
  StatValue,
  ReliabilityBox,
  formatPercentage,
  formatDelayMinutes,
  getColorVariants,
} from '../Elements/SharedStatComponents';

// Helper to get cancellation rate color
const getCancellationRateColor = (rate: number): string => {
  return getColor(rate, 'cancellationPercentage');
};

// Helper to get missed connections color
const getMissedConnectionsColor = (percentage: number): string => {
  return getColor(percentage, 'missedTransfersPercentage');
};

type ReliabilityScoreBadgeProps = {
  journey: Journey;
}

const ReliabilityScoreBadge = ({ journey }: ReliabilityScoreBadgeProps) => {
  const { t } = useTranslation();
  const reliability = journey.reliability;
  const score = reliability?.reliabilityScore;
  const completeness = reliability?.reliabilityScoreCompleteness || 'null';
  
  // Only show if reliability exists
  if (!reliability || score == null) {
    return null;
  }
  
  const reliabilityColor = getReliabilityColorGradient(score);
  const displayScore = Math.round(score);
  const displayText = `${displayScore}${completeness === 'partial' ? '*' : ''}`;
  
  const delayStats = journey.journeyDelayStats;
  const avgDelay = delayStats?.averageDelayAtDest;
  const cancellationRate = delayStats?.journeyCancellationRate;
  
  // Get highest missed connections percentage from transfer stats
  const nonNullTransferStats = journey.transferStats?.filter((stat): stat is NonNullable<typeof stat> => stat !== null) ?? [];
  const missedConnections = nonNullTransferStats.length > 0
    ? nonNullTransferStats
        .map(stat => stat.percentageMissedConnections)
        .filter((val): val is number => val != null)
        .reduce((max, val) => Math.max(max, val), 0)
    : null;
  
  // Tooltip content based on completeness
  const tooltipContent = (
    <TooltipContentWrapper>
      <TooltipMainText>
        {t('journeyDetails.reliabilityScoreBadge.tooltip')}
      </TooltipMainText>
      <TooltipSecondaryText className="tooltip-secondary-text">
        {t('journeyDetails.reliabilityScoreBadge.basedOnLast30Days')}
      </TooltipSecondaryText>
      {completeness === 'partial' && (
        <TooltipSecondaryText className="tooltip-secondary-text">
          {t('journeyDetails.reliabilityScoreBadge.tooltipSecondary')}
        </TooltipSecondaryText>
      )}
    </TooltipContentWrapper>
  );

  // Function to handle click event and stop propagation
  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };
  
  const hasStats = avgDelay != null || cancellationRate != null || missedConnections != null;
  
  return (
    <>
      <TooltipGlobalStyle />
      <StatContainer 
        $clickable
        data-tooltip-id={`reliability-${displayScore}`} 
        onClick={handleClick}
      >
          <ReliabilityBox $bgColor={reliabilityColor}>
          <StatValue $valueColor={colors.white} $large $white>
            {displayText}
          </StatValue>
          <StatTitle $white>chuuchuu score</StatTitle>
        </ReliabilityBox>
        
        {hasStats && (
          <StatsContainer $flex>
            {avgDelay != null && (() => {
              const delayMinutes = Math.round(avgDelay / 60);
              const delayColor = getColor(delayMinutes, 'delayInMinutes');
              const delayValueColor = getColorVariants(delayColor);
              return (
                <StatBox $flex $gap="0.125rem">
                  <StatTopSection>
                    <StatIconWrapper $iconColor={delayColor}>
                      <Timer size={16} />
                    </StatIconWrapper>
                    <StatValue $valueColor={delayValueColor}>
                      {formatDelayMinutes(avgDelay)}
                    </StatValue>
                  </StatTopSection>
                  <StatTitle>avg delay</StatTitle>
                </StatBox>
              );
            })()}
            
            {missedConnections != null && (() => {
              const missedColor = getMissedConnectionsColor(missedConnections);
              const missedValueColor = getColorVariants(missedColor);
              return (
                <StatBox $flex $gap="0.125rem">
                  <StatTopSection>
                    <StatIconWrapper $iconColor={missedColor}>
                      <ArrowRightLeft size={16} />
                    </StatIconWrapper>
                    <StatValue $valueColor={missedValueColor}>
                      {formatPercentage(missedConnections)}
                    </StatValue>
                  </StatTopSection>
                  <StatTitle>missed</StatTitle>
                </StatBox>
              );
            })()}
            
            {cancellationRate != null && (() => {
              const cancelColor = getCancellationRateColor(cancellationRate);
              const cancelValueColor = getColorVariants(cancelColor);
              return (
                <StatBox $flex $gap="0.125rem">
                  <StatTopSection>
                    <StatIconWrapper $iconColor={cancelColor}>
                      <XCircle size={16} />
                    </StatIconWrapper>
                    <StatValue $valueColor={cancelValueColor}>
                      {formatPercentage(cancellationRate)}
                    </StatValue>
                  </StatTopSection>
                  <StatTitle>cancelled</StatTitle>
                </StatBox>
              );
            })()}
          </StatsContainer>
        )}
      </StatContainer>
      <StyledTooltip id={`reliability-${displayScore}`} place="top" className="tooltip-styled">
        {tooltipContent}
      </StyledTooltip>
    </>
  )
}

export default ReliabilityScoreBadge;
